import { useCallback, useEffect, useMemo, useState } from 'react';
import { localStorageBridge } from 'shared/src/utils';

const LAST_VIEWED_KEY_PREFIX = 'mumul_live_contents_last_viewed_at';

const getStorageKey = (profileSno: number) =>
  `${LAST_VIEWED_KEY_PREFIX}_${profileSno}`;

/**
 * 콘텐츠 더보기 NEW 뱃지 노출 여부.
 * 마지막 확인 시점(로컬 저장)보다 최신 콘텐츠가 있으면 노출하고,
 * 더보기 진입 시 그 시점의 최신 `posted_at` 을 확인 시점으로 저장한다.
 */
export const useMumulLiveContentsNewBadge = (
  profileSno: number | null,
  latestPostedAt: string | null,
) => {
  const [lastViewedAt, setLastViewedAt] = useState<string | null>(null);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    if (profileSno == null) return;

    let canceled = false;
    localStorageBridge.getString(getStorageKey(profileSno)).then(stored => {
      if (canceled) return;
      setLastViewedAt(stored);
      setIsLoaded(true);
    });

    return () => {
      canceled = true;
    };
  }, [profileSno]);

  // posted_at 은 타임존 없는 동일 포맷이라 문자열 비교로 시점 비교가 가능하다.
  const hasNewContents = useMemo(() => {
    if (!isLoaded || latestPostedAt == null) return false;
    if (lastViewedAt == null) return true;

    return latestPostedAt > lastViewedAt;
  }, [isLoaded, latestPostedAt, lastViewedAt]);

  // 저장만 하고 뱃지는 그대로 둔다. 다음 화면 진입 시 저장값을 읽어 판별한다.
  const markContentsAsViewed = useCallback(() => {
    if (profileSno == null || latestPostedAt == null) return;

    localStorageBridge.putString(getStorageKey(profileSno), latestPostedAt);
  }, [profileSno, latestPostedAt]);

  return { hasNewContents, markContentsAsViewed };
};
